import PageContainer from '../components/PageContainer';
import PageHero from '../components/PageHero';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { TrendingUp, Target, DollarSign, Users, CheckCircle, BarChart, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForProviders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  usePageTitle('Für Dienstleister');

  const [plans, setPlans] = useState({});
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await axios.get(`${API}/subscription/plans`);
        setPlans(response.data.plans || {});
      } catch (error) {
        console.error('Error loading plans:', error);
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, []);

  // Early Adopter is an internal offer, not part of the public line-up
  const publicPlans = Object.entries(plans).filter(([planId]) => planId !== 'early_adopter_basic');

  const steps = [
    {
      number: 1,
      title: 'Profil kostenlos erstellen',
      description: 'Registriere dich in wenigen Minuten und lege dein Dienstleister-Profil an'
    },
    {
      number: 2,
      title: 'Kategorien auswählen',
      description: 'Wähle deine Hauptkategorien und spezialisiere dich mit Unterkategorien'
    },
    {
      number: 3,
      title: 'Portfolio präsentieren',
      description: 'Lade deine besten Arbeiten hoch und beschreibe deine Dienstleistungen'
    },
    {
      number: 4,
      title: 'Anfragen erhalten',
      description: 'Kunden finden dich über die Suche und senden dir direkt Anfragen'
    },
    {
      number: 5,
      title: 'Abo aktivieren',
      description: 'Schalte dein Profil ab 16,99 €/Monat frei - monatlich kündbar'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Mehr Sichtbarkeit',
      description: 'Erreiche potenzielle Kunden, die aktiv nach Dienstleistern wie dir suchen'
    },
    {
      icon: Target,
      title: 'Zielgerichtete Anfragen',
      description: 'Erhalte qualifizierte Anfragen von Kunden, die genau deine Services benötigen'
    },
    {
      icon: DollarSign,
      title: 'Planbare Kosten',
      description: 'Ab 16,99 € pro Monat - keine versteckten Gebühren, monatlich kündbar'
    },
    {
      icon: Users,
      title: 'Stammkunden gewinnen',
      description: 'Baue langfristige Kundenbeziehungen auf und gewinne wiederkehrende Aufträge'
    },
    {
      icon: CheckCircle,
      title: 'Professionelle Präsenz',
      description: 'Auch ohne eigene Website professionell auftreten und gefunden werden'
    },
    {
      icon: BarChart,
      title: 'Zusatzkanal',
      description: 'Erweitere deine Reichweite ohne komplexes Marketing oder hohe Werbekosten'
    }
  ];

  const features = [
    'Vollständig anpassbares Profil mit Portfolio',
    'Unbegrenzt Bilder hochladen',
    'Direkte Kundenanfragen mit Event-Details',
    'Links zu Instagram und deiner Website',
    'Übersichtliches Dashboard zur Verwaltung',
    'Monatlich kündbar - keine Mindestlaufzeit'
  ];

  return (
    <div className="min-h-screen bg-[#F4F2F2]">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Hero Section */}
      <PageHero
        title="Mehr Sichtbarkeit für dein Event-Business"
        subtitle="Erreiche Kunden, die aktiv nach Dienstleistern wie dir suchen – lokal und zielgerichtet."
        buttonText="Jetzt kostenlos starten"
        buttonOnClick={() => navigate('/register?role=provider')}
        buttonTestId="start-provider-btn"
      >
        <p className="text-sm text-gray-500 mt-4">Profilerstellung kostenlos • ab 16,99 €/Monat für Sichtbarkeit</p>
      </PageHero>

      {/* How it works */}
      <section className="py-20 px-4 bg-white">
        <div className="container max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">So startest du auf EventConnect</h2>
          
          <div className="space-y-6">
            {steps.map((step) => (
              <div key={step.number} className="flex items-start gap-6 p-6 bg-[#FFF0D1]/50 rounded-2xl">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2B2142] rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                  {step.number}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4">
        <div className="container max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">Deine Vorteile als Dienstleister</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-[#2B2142] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-white">
        <div className="container max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Was du bekommst</h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 bg-[#FFF0D1]/50 rounded-xl">
                <CheckCircle className="w-6 h-6 text-[#F7B693] flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4">
        <div className="container max-w-6xl">
          <div className="bg-[#2B2142] rounded-3xl p-6 sm:p-10 md:p-12 text-white shadow-2xl">
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-4">Transparente Preise</h2>
              <p className="text-xl opacity-90 mb-10">
                Keine Setup-Gebühren, keine versteckten Kosten. Starte kostenlos und aktiviere dein Abo, wenn du bereit bist.
              </p>
            </div>

            {plansLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[0, 1, 2, 3].map((idx) => (
                  <div key={idx} className="bg-white/10 rounded-2xl h-80 animate-pulse" />
                ))}
              </div>
            ) : publicPlans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {publicPlans.map(([planId, plan]) => (
                  <div
                    key={planId}
                    className="bg-white rounded-2xl p-6 text-left text-gray-900 flex flex-col shadow-lg"
                  >
                    <h3 className="text-xl font-bold mb-3">{plan.name}</h3>
                    <div className="text-3xl font-bold">
                      {plan.price.toFixed(2).replace('.', ',')} € / {plan.interval === 'month' ? 'Monat' : 'Jahr'}
                    </div>
                    <p className="text-sm text-gray-500 mt-1 mb-4">zzgl. 19% MwSt.</p>
                    <p className="text-xs text-gray-600 mb-5">
                      {plan.interval === 'month'
                        ? 'Monatliche Abrechnung, kündbar zum Monatsende'
                        : 'Jahresbindung, kündbar zum Ende des Jahres'}
                    </p>
                    <ul className="space-y-2 mb-6 flex-grow">
                      {(plan.features || []).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate('/register?role=provider')}
                      className="w-full py-3 bg-[#F7B693] text-[#2B2142] font-semibold rounded-xl hover:bg-[#E9A07D] transition-colors"
                      data-testid={`pricing-cta-${planId}`}
                    >
                      Jetzt starten
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              /* Fallback if the plans API is unreachable */
              <div className="text-center">
                <div className="text-6xl font-bold mb-2">ab 16,99 €</div>
                <p className="text-xl opacity-90">pro Monat • monatlich kündbar</p>
              </div>
            )}

            <div className="text-center mt-10">
              <button
                onClick={() => navigate('/pricing')}
                className="text-[#F7B693] underline underline-offset-4 hover:text-white transition-colors"
                data-testid="pricing-compare-link"
              >
                Alle Details vergleichen
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForProviders;
